import React, { useCallback, useMemo, useState } from 'react';
import { useAsyncValue, useNavigate } from 'react-router-dom';
import { useRevalidateOnSync } from '../../../../../offline/useRevalidateOnSync';
import { useTranslation } from 'react-i18next';

// Styles
import { Container } from './styles';
import { ContainerButton } from '../../components/MainStyledComponents';

// Components
import { InputRadioTagTable } from '../../../../../components/display/tables/InputRadioTagTable';
import { tableProps as InputRadioTableProps } from '../../../../../components/display/tables/InputRadioTagTable/types';
import { MainButton } from '../../../../../components/inputs/buttons/MainButton';

// Types
import { tableIds } from '../../../../../components/display/tables/types';
import { SurveillanceRoutesLoaderData } from '../../types';
import { Role } from '../../../../../types/auth';

// Constants
import { SR_HIDDEN_COLUMN_IDS, SR_HIDDEN_FILTER_IDS } from '../../constants';

// Utils
import {
  canEditSurveillanceRoute,
  getSurveillanceRouteFlagOrderId,
} from '../../../../../utils/surveillanceRoute';

// Hooks
import { useAuth } from '../../../../../hooks/useAuth';
import { useSurveillanceRouteOwnership } from '../../../../../hooks/useSurveillanceRouteOwnership';
import { useSurveillanceRoutesListing } from '../../../../../hooks/useSurveillanceRoutesListing';

// Ruta de este listado. El wizard EditRoute vuelve aquí al guardar vía su
// `?returnTo=` (ver `EditRoute/action.ts`), así que el usuario acaba donde
// empezó y no en el detalle.
const ROUTES_LIST_PATH = '/surveillance-route';

// Adapter moved to `src/utils/surveillanceRoute.ts` as
// `adaptSurveillanceRoutesLoaderData`. It produces Order-compatible row shapes
// (strings, not objects) y mapea `state` (V4 2026-04-21) a un variant válido de
// SpecialTag para que las columnas de la tabla genérica no crasheen.

export const SurveillanceRoutesPage = () => {
  const { t } = useTranslation(['pages', 'workOrders']);
  const rawData = useAsyncValue() as SurveillanceRoutesLoaderData;
  useRevalidateOnSync();
  const [selectedRowId, setSelectedRowId] = useState<string>('');
  const navigate = useNavigate();

  // Cursor Redux + polling + refetch — extraído a hook compartido con
  // SearchAndCreateHistoricalRoute (Task 2, refactor de patrones RV
  // 2026-07-07). Ver JSDoc de `useSurveillanceRoutesListing` para la única
  // diferencia real de comportamiento preservada entre ambos listados.
  const { adaptedData, onReFetchData, onRefetchPagination } =
    useSurveillanceRoutesListing(rawData, tableIds.SURVEILLANCE_ROUTES);

  const { user } = useAuth();
  const currentRole = user?.user?.rolActive as Role | undefined;

  // "Editar" (2026-08-04): el botón vivía solo en el histórico de Informes,
  // que es una sección de consulta y a la que Tablet ni siquiera entra — la
  // patrulla solo podía editar su recorrido desde el lápiz del detalle. Mismo
  // gating rol×estado que usan el histórico y esa cabecera: nunca en
  // Finalizada/Anulada, y Tablet solo en Abierta/En curso.
  //
  // La tabla solo devuelve el id por `setRowState`, así que el estado se busca
  // en la fila adaptada, donde viaja como `specialTag`.
  const selectedRouteRow = useMemo(() => {
    if (!selectedRowId) return undefined;
    return (adaptedData.genericOrders || []).find(
      (row: { id?: string }) => row.id === selectedRowId
    ) as { id?: string; specialTag?: string } | undefined;
  }, [adaptedData.genericOrders, selectedRowId]);

  // La fila adaptada no trae equipo ni dueño: se leen de la caché, donde el
  // loader del listado acaba de dejar el recorrido (2026-09-23).
  const selectedRouteOwnership = useSurveillanceRouteOwnership(selectedRowId);
  const canEditSelectedRoute = canEditSurveillanceRoute(
    currentRole,
    selectedRouteRow?.specialTag,
    user?.user,
    selectedRouteOwnership
  );

  const handleEditRoute = useCallback(() => {
    if (!selectedRowId) return;
    navigate(
      `/surveillance-route/${selectedRowId}/edit?returnTo=${encodeURIComponent(
        ROUTES_LIST_PATH
      )}`
    );
  }, [selectedRowId, navigate]);

  const handleViewRoute = useCallback(() => {
    if (selectedRowId) {
      navigate(`/surveillance-route/${selectedRowId}`);
    }
  }, [selectedRowId, navigate]);

  return (
    <Container>
      <InputRadioTagTable
        titleText={t('pages:surveillance.filterBy')}
        data={adaptedData as unknown as InputRadioTableProps['data']}
        setRowState={setSelectedRowId}
        onReFetchData={onReFetchData}
        onReFetchPagination={onRefetchPagination}
        tableId={tableIds.SURVEILLANCE_ROUTES}
        // V4 FR-1.6 / FR-1.5: columnas y filtros no aplicables al dominio RV
        // (constantes compartidas con SearchAndCreateHistoricalRoute).
        hiddenColumnIds={SR_HIDDEN_COLUMN_IDS}
        hiddenFilterIds={SR_HIDDEN_FILTER_IDS}
        // V4 FR-1.6: añade columna "Nom del recorregut" tras ROID.
        showRouteNameColumn={true}
        // 2026-07-17: columna "Creado por" (Order gemela userId → nombre,
        // adapter creatorName). Solo RV.
        showCreatorColumn={true}
        // 2026-07-12 (feedback usuario): filtro Operarios como en Actuaciones
        // (multi-select RV.userIDs + toggle "incluir inactivos en Operarios").
        showOperatorsFilter={true}
        // V4 FR-1.7 (2026-05-27): doble-click fila → detalle SR. Sin
        // esta prop, la tabla shared usaría su default
        // `/work-orders/${id}` (válido solo para WorkOrders Vista
        // general; aquí daría URL rota).
        onRowDoubleTap={(row) => {
          if (row.original.id) navigate(`/surveillance-route/${row.original.id}`);
        }}
        // Banderitas (spec 2026-10-22 § 8.4): columna y filtro «Solo marcadas».
        // La fila lleva en `id` el del recorrido; la bandera marca su Order.
        showFlagColumn
        showOnlyFlaggedFilter
        getFlagOrderId={getSurveillanceRouteFlagOrderId}
      />
      <ContainerButton>
        <MainButton
          variant="secondary"
          text={t('common:buttons.edit')}
          disabled={!canEditSelectedRoute}
          onClick={handleEditRoute}
        />
        <MainButton
          text={t('pages:surveillance.seeRoute')}
          disabled={selectedRowId.length === 0}
          onClick={handleViewRoute}
        />
      </ContainerButton>
    </Container>
  );
};
